import { z } from 'zod';
import { dateTimeSchema, idSchema, nullableDateTimeSchema } from './common';
import { roleSchema } from './enums';

/** SPEC §9 — auth. Cookie name is fixed; the session id itself never appears in a body. */
export const SESSION_COOKIE_NAME = 'tekvoice_session';
export const SESSION_TTL_DAYS = 30;
export const INVITE_TTL_DAYS = 7;
export const LOGIN_RATE_LIMIT_PER_MINUTE = 10;

export const passwordSchema = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .max(200);

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .max(254);

/** The authenticated principal, as returned by GET /api/auth/me. */
export const sessionUserSchema = z.object({
  id: idSchema,
  email: z.string().email(),
  name: z.string(),
  role: roleSchema,
  createdAt: dateTimeSchema,
  disabledAt: nullableDateTimeSchema,
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

/** POST /api/auth/login */
export const loginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const loginResponseSchema = z.object({
  user: sessionUserSchema,
  expiresAt: dateTimeSchema,
});
export type LoginResponse = z.infer<typeof loginResponseSchema>;

/** GET /api/auth/me */
export const meResponseSchema = z.object({
  user: sessionUserSchema,
  expiresAt: dateTimeSchema,
});
export type MeResponse = z.infer<typeof meResponseSchema>;

/** POST /api/auth/logout */
export const logoutResponseSchema = z.object({ ok: z.literal(true) });
export type LogoutResponse = z.infer<typeof logoutResponseSchema>;

/** GET /api/auth/accept-invite?token=… — validate a token before showing the form. */
export const inviteLookupQuerySchema = z.object({ token: z.string().min(10).max(200) });
export type InviteLookupQuery = z.infer<typeof inviteLookupQuerySchema>;

export const inviteLookupResponseSchema = z.object({
  valid: z.boolean(),
  email: z.string().email().nullable(),
  role: roleSchema.nullable(),
  expiresAt: nullableDateTimeSchema,
  /** Populated when `valid` is false: `not_found` | `expired` | `already_used`. */
  reason: z.enum(['not_found', 'expired', 'already_used']).nullable(),
});
export type InviteLookupResponse = z.infer<typeof inviteLookupResponseSchema>;

/** POST /api/auth/accept-invite — sets name + password and logs the user in. */
export const acceptInviteRequestSchema = z.object({
  token: z.string().min(10).max(200),
  name: z.string().trim().min(1).max(120),
  password: passwordSchema,
});
export type AcceptInviteRequest = z.infer<typeof acceptInviteRequestSchema>;

export const acceptInviteResponseSchema = z.object({
  user: sessionUserSchema,
  expiresAt: dateTimeSchema,
});
export type AcceptInviteResponse = z.infer<typeof acceptInviteResponseSchema>;
